"use client";

/**
 * <ComponentRecordCard> — the interactive component-record mock in the
 * landing page's § 02 · Components showcase.
 *
 * A small client island (tab state only) so app/page.tsx can stay a
 * server component. Everything rendered here is ILLUSTRATIVE sample
 * data, but every row is constrained to things the product actually
 * does — see the landing-page content policy in app/page.tsx:
 *   - History rows use only real journaled event types (lifecycle
 *     change, lead-time override, datasheet linked, …).
 *   - Sourcing figures carry "est." qualifiers — estimates, not quotes.
 *   - The Overview decision block is a freeform team NOTE ("Team note"),
 *     which is what the product stores — not a structured decision object.
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { Pill } from "@/components/shared/pill";

const TABS = [
  "Overview",
  "Alternates",
  "Sourcing",
  "Datasheets",
  "History",
] as const;

type Tab = (typeof TABS)[number];

// --- sample data (illustrative; truthful event/feature types only) ---

const BOM_USAGE: Array<[string, string]> = [
  ["SENTINEL-V3 · rev F", "qty 1"],
  ["SENTINEL-V384 · rev C", "qty 1"],
  ["ORCA-2 · rev B", "qty 2"],
];

const OPEN_IMPACTS: Array<[string, string]> = [
  ["ST PCN #DSC-2026-0871 · process change", "owner Sarah · due 2026-08-21"],
  ["Distributor lifecycle signal · NRND", "owner Mason · under review"],
];

const ALTERNATES: Array<{
  mpn: string;
  desc: string;
  mfrRec?: boolean;
  match: string;
}> = [
  {
    mpn: "STM32F407VET6",
    desc: "STMicroelectronics · lower flash, same family",
    mfrRec: true,
    match: "96%",
  },
  {
    mpn: "STM32F427VGT6",
    desc: "STMicroelectronics · superset, active",
    match: "93%",
  },
  {
    mpn: "GD32F407VGT6",
    desc: "GigaDevice · pin-map match, different vendor",
    match: "87%",
  },
];

const SOURCING_ROWS: Array<{
  dist: string;
  kind: "authorized" | "independent";
  stock: string;
  moq: string;
  price: string;
  lead: string;
}> = [
  { dist: "DigiKey", kind: "authorized", stock: "418", moq: "1", price: "$9.62", lead: "22 wk" },
  { dist: "Mouser", kind: "authorized", stock: "0", moq: "1", price: "$9.98", lead: "26 wk" },
  { dist: "Sourcengine", kind: "independent", stock: "2,100", moq: "100", price: "$14.20", lead: "5 wk" },
];

// Only truthful document sources: datasheets pulled via DigiKey or the
// parts database, and the source notice itself. No invented doc types.
const DATASHEETS: Array<[string, string, boolean]> = [
  ["STM32F407xx datasheet rev 9", "via DigiKey", false],
  ["STM32F427xx datasheet rev 6", "via parts database", false],
  ["ST PCN #DSC-2026-0871", "source notice", false],
];

// Real journaled event types only: lifecycle change, datasheet linked,
// lead-time override.
const HISTORY: Array<{
  when: string;
  who: string;
  what: React.ReactNode;
  why: string;
}> = [
  {
    when: "Aug 12",
    who: "Mason",
    what: (
      <>
        Lifecycle changed{" "}
        <span className="font-mono">Active &rarr; NRND</span>
      </>
    ),
    why: "Source: ST PCN #DSC-2026-0871",
  },
  {
    when: "Aug 14",
    who: "Sarah",
    what: (
      <>
        Datasheet linked{" "}
        <span className="font-mono">STM32F407xx rev 9</span>
      </>
    ),
    why: "Source: DigiKey",
  },
  {
    when: "Aug 15",
    who: "John",
    what: (
      <>
        Lead-time override{" "}
        <span className="font-mono">26 wk &rarr; 22 wk</span>
      </>
    ),
    why: "Source: supplier quote",
  },
];

// --- component ------------------------------------------------------

export function ComponentRecordCard() {
  const [tab, setTab] = React.useState<Tab>("Overview");

  return (
    <div className="border rule bg-white shadow-[0_1px_0_rgba(0,0,0,0.04),0_24px_60px_-30px_rgba(15,23,42,0.25)]">
      {/* header */}
      <div className="px-5 sm:px-7 pt-6 pb-5 border-b rule">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
          <span className="font-mono text-[20px] sm:text-[23px] font-medium tracking-tight">
            STM32F407VGT6
          </span>
          <span className="text-[13px] ink-3">
            STMicroelectronics · 32-bit MCU, LQFP-100
          </span>
          <span className="flex-1" />
          <Pill tone="alert">NRND</Pill>
        </div>

        {/* stat row */}
        <div className="mt-5 pt-4 border-t rule grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-4">
          <StatCell label="Lifecycle" value="NRND" sub="set manually" alert />
          <StatCell label="Used in" value="12 BOMs" sub="4 programs" />
          <StatCell label="Open impacts" value="2" sub="1 due in 6 days" alert />
          <StatCell label="Lead time" value="22 weeks" sub="estimated" />
          <StatCell label="Market @1k" value="$9.84" sub="median, estimated" />
        </div>

        {/* lifecycle rail */}
        <div className="mt-5 grid grid-cols-4 gap-1.5">
          <RailSegment label="Active" state="past" />
          <RailSegment label="NRND · current" state="current" />
          <RailSegment label="EOL" state="future" />
          <RailSegment label="Obsolete" state="future" />
        </div>
      </div>

      {/* tabs */}
      <div
        role="tablist"
        aria-label="Component record sections"
        className="flex gap-0.5 px-4 sm:px-6 border-b rule bg-paper-2 overflow-x-auto"
      >
        {TABS.map((t) => {
          const active = t === tab;
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t)}
              className={cn(
                "-mb-px shrink-0 px-4 py-3 text-[13px] font-medium border-t-2 transition-colors",
                active
                  ? "bg-white border-t-[color:var(--shark-accent-ink)] border-x rule text-[color:var(--ink)]"
                  : "border-t-transparent border-x border-x-transparent ink-3 hover:text-[color:var(--ink)]",
              )}
            >
              {t}
            </button>
          );
        })}
      </div>

      {/* panel */}
      <div className="px-5 sm:px-7 py-6 min-h-[240px]">
        {tab === "Overview" && <OverviewPanel />}
        {tab === "Alternates" && <AlternatesPanel />}
        {tab === "Sourcing" && <SourcingPanel />}
        {tab === "Datasheets" && <DatasheetsPanel />}
        {tab === "History" && <HistoryPanel />}
      </div>
    </div>
  );
}

// --- pieces ---------------------------------------------------------

function StatCell({
  label,
  value,
  sub,
  alert,
}: {
  label: string;
  value: string;
  sub: string;
  alert?: boolean;
}) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-wider ink-3">
        {label}
      </div>
      <div
        className={cn(
          "mt-1.5 text-[16px] font-semibold tracking-tight",
          alert && "text-alert",
        )}
      >
        {value}
      </div>
      <div className="mt-0.5 font-mono text-[10.5px] ink-3">{sub}</div>
    </div>
  );
}

function RailSegment({
  label,
  state,
}: {
  label: string;
  state: "past" | "current" | "future";
}) {
  return (
    <div>
      <div
        className="h-[5px]"
        style={{
          background:
            state === "past"
              ? "var(--shark-good)"
              : state === "current"
                ? "var(--shark-alert)"
                : "var(--rule)",
        }}
      />
      <div
        className={cn(
          "mt-1.5 font-mono text-[9.5px] uppercase tracking-wider whitespace-nowrap",
          state === "current" ? "text-alert font-medium" : "ink-3",
          state === "future" && "opacity-60",
        )}
      >
        {label}
      </div>
    </div>
  );
}

function PanelLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-wider ink-3">
      {children}
    </div>
  );
}

function OverviewPanel() {
  return (
    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      <div>
        <PanelLabel>BOM usage</PanelLabel>
        <div className="mt-3 border-t rule">
          {BOM_USAGE.map(([bom, qty]) => (
            <div
              key={bom}
              className="flex items-center gap-3 py-2 border-b rule text-[13px]"
            >
              <span className="font-mono flex-1 min-w-0 truncate">{bom}</span>
              <span className="ink-3">{qty}</span>
            </div>
          ))}
          <div className="py-2 text-[13px] ink-3">+ 9 more BOMs</div>
        </div>
      </div>

      <div>
        <PanelLabel>Open impacts</PanelLabel>
        <div className="mt-3 border-t rule">
          {OPEN_IMPACTS.map(([title, meta]) => (
            <div key={title} className="py-2.5 border-b rule last:border-b-0">
              <div className="text-[13px]">{title}</div>
              <div className="mt-0.5 font-mono text-[10.5px] ink-3">{meta}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        {/* Freeform prose written by a person — labelled as a note, not a
            structured "decision" object. */}
        <PanelLabel>Team note</PanelLabel>
        <div className="mt-3 border rule bg-paper-2 p-4">
          <p className="text-[13px] leading-relaxed ink-2">
            Lifecycle set to NRND on 2026-08-12 by Mason, on evidence from ST
            PCN #DSC-2026-0871. Preferred alternate recorded for Rev C designs.
            No new starts on this part.
          </p>
          <div className="mt-2.5 font-mono text-[10.5px] ink-3">
            1 evidence file attached
          </div>
        </div>
      </div>
    </div>
  );
}

function AlternatesPanel() {
  return (
    <div className="border-t rule">
      {ALTERNATES.map((a) => (
        <div
          key={a.mpn}
          className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-3.5 border-b rule last:border-b-0"
        >
          <div className="flex-1 min-w-[200px]">
            <div className="font-mono text-[13.5px]">{a.mpn}</div>
            <div className="mt-0.5 text-[12.5px] ink-3">{a.desc}</div>
          </div>
          {a.mfrRec && <Pill tone="info">Mfr-recommended</Pill>}
          <span className="font-mono text-[12px] ink-2 w-14 text-right">
            {a.match}
          </span>
        </div>
      ))}
    </div>
  );
}

function SourcingPanel() {
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[540px] border-t rule">
        <div className="grid grid-cols-[minmax(96px,1.2fr)_100px_minmax(60px,0.7fr)_48px_minmax(64px,0.7fr)_minmax(64px,0.7fr)] gap-x-4 py-2 border-b rule font-mono text-[10px] uppercase tracking-wider ink-3 whitespace-nowrap">
          <span>distributor</span>
          <span>type</span>
          <span>stock</span>
          <span>moq</span>
          <span>@1k est.</span>
          <span>lead est.</span>
        </div>
        {SOURCING_ROWS.map((r) => (
          <div
            key={r.dist}
            className={cn(
              "grid grid-cols-[minmax(96px,1.2fr)_100px_minmax(60px,0.7fr)_48px_minmax(64px,0.7fr)_minmax(64px,0.7fr)] gap-x-4 py-2.5 border-b rule last:border-b-0 items-center text-[13px]",
              r.kind === "independent" && "ink-3",
            )}
          >
            <span>{r.dist}</span>
            <Pill tone={r.kind === "authorized" ? "info" : "neutral"}>
              {r.kind}
            </Pill>
            <span className="font-mono">{r.stock}</span>
            <span className="font-mono">{r.moq}</span>
            <span className="font-mono">{r.price}</span>
            <span className="font-mono">{r.lead}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DatasheetsPanel() {
  return (
    <div className="border-t rule">
      {DATASHEETS.map(([name, source]) => (
        <div
          key={name}
          className="flex items-center gap-4 py-3 border-b rule last:border-b-0"
        >
          <span className="font-mono text-[10px] ink-3 w-9 shrink-0">PDF</span>
          <span className="flex-1 text-[13px] min-w-0 truncate">{name}</span>
          <span className="font-mono text-[10.5px] ink-3 whitespace-nowrap">
            {source}
          </span>
        </div>
      ))}
    </div>
  );
}

function HistoryPanel() {
  return (
    <div className="border-t rule">
      {HISTORY.map((h) => (
        <div
          key={h.when}
          className="grid grid-cols-[96px_minmax(0,1fr)] sm:grid-cols-[150px_minmax(0,1fr)] gap-4 py-3.5 border-b rule last:border-b-0"
        >
          <div className="font-mono text-[11.5px] ink-3">
            {h.when} · {h.who}
          </div>
          <div>
            <div className="text-[13.5px]">{h.what}</div>
            <div className="mt-0.5 text-[12.5px] ink-3">{h.why}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
